import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import SelectPlan from '../select-plan';
import type { IProps } from './modify-inbox-radar-subscription-container';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { ModifySubscriptionSteps, SetPlanDetails } from './types';
import RadioButtonTransfer from '../../../../../../shared/design-system/components/atoms/radio-button-transfer';
import {
  billingOptions,
  gePlanCycleBasedOnPlanType,
  getHighlightMessage,
  getDisabledLabel,
  isBillingAndSubscriptionPageReady,
  isInboxRadarBasedPlanVisible,
} from './utlis/helper';
import {
  ModifyPlanAction,
  PlanType,
  SubscriptionPlanModes,
  SubscriptionPlans,
} from '../../../../../../shared/utils/subscription-plans';
import ImageIcon from '../../../../../../shared/components/images/image-icon';
import { Images } from '../../../../../../shared/app-constants';
import Spinner from '../../../../../../shared/design-system/components/atoms/custom-spinner';
import { getPlanMetaData } from '../select-plan/utils/get-plan-meta-data';
import {
  getPlanId,
  getPlanNameByPlanCycle,
  getPlanNameByPlanMode,
} from '../upgrade-plan/utils/helper';
import { getPlanCode } from '../select-plan/utils/helper';
import PaymentFailed from '../upgrade-plan/payment-failed';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import { showGeneralErrorNotification } from '../../../../../../shared/utils/errors';
import InboxRadarPlans from '../inbox-radar-card';
import InboxRadarUpgradePlan from '../inbox-radar-upgrade-plan';

const ModifyInboxRadarSubscription: React.FC<IProps> = (containerProps) => {
  const { t } = useTranslation();
  const location = useLocation<{
    modifySubscriptionStep: ModifySubscriptionSteps;
    isRedirectedFromBillingOptions: boolean;
    isRedirectedFromEmailAccounts?: boolean;
  }>();

  // Select Plan
  const {
    selectPlan,
    selectedPlanDetails,
    sendGetCalculateInboxAnalyzerPayRequest,
    sendGetInboxAnalyzerSubscriptionResponse,
    sendGetInboxAnalyzerPlansResponse,
    userCurrentPlan,
    sendPurchaseInboxAnalyzerSubscriptionRequestStatus,
    sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
    resetPurchaseModifySubscriptionResponse,
    resetCalculateInboxAnalyzerPayState,
    sendGetInboxAnalyzerSubscriptionRequestStatus,
    sendGetInboxAnalyzerPlansRequestStatus,
    subscriptionPlanStatus,
  } = containerProps;

  // States
  const [
    modifySubscriptionStep,
    setModifySubscriptionStep,
  ] = useState<ModifySubscriptionSteps>(ModifySubscriptionSteps.SelectPlan);
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    gePlanCycleBasedOnPlanType(
      sendGetInboxAnalyzerSubscriptionResponse?.planCode,
      sendGetInboxAnalyzerSubscriptionResponse?.planMode,
    ),
  );
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);

  const setPlanDetails = ({
    planCode,
    planType,
    planMode,
    planName,
    action,
  }: SetPlanDetails) => {
    const {
      planName: currentPlanName,
      planMode: currentPlanMode,
      planAmount,
      planId,
    } = sendGetInboxAnalyzerSubscriptionResponse || {};

    const getPlanName = getPlanNameByPlanMode(
      sendGetInboxAnalyzerPlansResponse,
      planCode,
      planMode,
    );

    let modifyAction = action;

    if (
      currentPlanMode === SubscriptionPlanModes.MonthlyMode &&
      planCycle === BillingOption.ANNUALLY
    ) {
      modifyAction = ModifyPlanAction.Update;
    } else if (currentPlanName === planName) {
      modifyAction = ModifyPlanAction.AddSlots;
      resetCalculateInboxAnalyzerPayState();
    }

    const { displayName, price } =
      getPlanMetaData(planCode, planCycle, planName || getPlanName) || {};

    selectPlan({
      displayName: displayName || planName,
      planId:
        getPlanId(
          sendGetInboxAnalyzerPlansResponse,
          planName || getPlanName,
          planCode,
          planType,
        ) || planId,
      planPrice: price || Number(planAmount),
      planCode,
      planType,
      action: modifyAction,
    });
  };

  const handleModifySubscriptionStep = (step) =>
    setModifySubscriptionStep(step);

  const handelBillingOption = (value) => setPlanCycle(value);

  const handelSelectPlan = (
    planCode: SubscriptionPlans,
    planType: PlanType,
    planName,
    planMode: number,
    action,
  ) => {
    const planId = getPlanId(
      sendGetInboxAnalyzerPlansResponse,
      planName,
      planCode,
      planType,
    );

    setPlanDetails({ planCode, planType, planMode, planName, action });
    handleModifySubscriptionStep(ModifySubscriptionSteps.SelectSlots);

    if (action === ModifyPlanAction.AddSlots) {
      resetCalculateInboxAnalyzerPayState();
    }

    if (
      action !== ModifyPlanAction.AddSlots &&
      planCycle !== BillingOption.LIFETIME &&
      userCurrentPlan !== SubscriptionPlans.FREE &&
      userCurrentPlan !== SubscriptionPlans.TRIAL
    ) {
      sendGetCalculateInboxAnalyzerPayRequest({
        accountSubscriptionPlanId: planId,
        slots: sendGetInboxAnalyzerSubscriptionResponse?.slots,
      });
    }
  };

  const onPaymentFailed = () => {
    setIsPaymentFailed(true);
  };

  const onPaymentRetrieve = () => {
    setIsPaymentFailed(false);
    setModifySubscriptionStep(ModifySubscriptionSteps.SelectPlan);
  };

  useEffect(() => {
    const { modifySubscriptionStep: step } = location.state || {
      modifySubscriptionStep: null,
    };

    if (step) {
      resetCalculateInboxAnalyzerPayState();
      setModifySubscriptionStep(step);
    }
  }, []);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetInboxAnalyzerSubscriptionRequestStatus,
      onPending: () => {
        setIsPaymentFailed(false);
      },
      onSuccess: () => {
        if (sendGetInboxAnalyzerSubscriptionResponse) {
          const {
            planCode,
            planName,
          } = sendGetInboxAnalyzerSubscriptionResponse;
          setPlanCycle(
            gePlanCycleBasedOnPlanType(
              sendGetInboxAnalyzerSubscriptionResponse?.planCode,
              sendGetInboxAnalyzerSubscriptionResponse?.planMode,
            ),
          );
          setPlanDetails({
            planCode,
            planName,
            action: ModifyPlanAction.AddSlots,
          });
        }
      },
    });
  }, [
    sendGetInboxAnalyzerSubscriptionRequestStatus,
    sendGetInboxAnalyzerPlansRequestStatus,
  ]);

  useEffect(() => {
    if (selectedPlanDetails && sendGetInboxAnalyzerSubscriptionResponse) {
      const { planName, planCode } = sendGetInboxAnalyzerSubscriptionResponse;
      setPlanDetails({
        planCode:
          planCode === SubscriptionPlans.PAID_MONTHLY ||
          planCode === SubscriptionPlans.PAID_YEARLY
            ? getPlanCode(planCycle)
            : planCode,
        planName: getPlanNameByPlanCycle(
          selectedPlanDetails.displayName || planName,
          planCycle,
        ),
        action: selectedPlanDetails.action,
      });
    }
  }, [planCycle]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseInboxAnalyzerSubscriptionRequestStatus,
      onFailed: () => {
        containerProps.hideLoading();
        onPaymentFailed();
      },
    });
  }, [sendPurchaseInboxAnalyzerSubscriptionRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
      onFailed: () => {
        containerProps.hideLoading();
        onPaymentFailed();
        resetPurchaseModifySubscriptionResponse();
      },
    });
  }, [sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus]);

  useEffect(() => {
    const {
      sendGetInboxAnalyzerSubscriptionRequest,
      sendGetInboxAnalyzerPlansRequest,
    } = containerProps;
    sendGetInboxAnalyzerSubscriptionRequest();
    sendGetInboxAnalyzerPlansRequest();
  }, []);

  const {
    sendGetConnectedUsersAndEmailAccountsRequestError,
    sendGetConnectedUsersAndEmailAccountsRequestStatus,
    resetConnectedUsersAndEmailAccountsRequest,
  } = containerProps;

  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetConnectedUsersAndEmailAccountsRequestStatus,
      onSuccess: () => {
        resetConnectedUsersAndEmailAccountsRequest();
      },
      onFailed: () => {
        showGeneralErrorNotification(
          sendGetConnectedUsersAndEmailAccountsRequestError?.message,
        );
        resetConnectedUsersAndEmailAccountsRequest();
      },
    });
  }, [sendGetConnectedUsersAndEmailAccountsRequestStatus]);

  // Check it payment failed or not
  if (isPaymentFailed) {
    return <PaymentFailed onRetryPayment={() => onPaymentRetrieve()} />;
  }

  const isRequestPending = isBillingAndSubscriptionPageReady({
    sendGetInboxAnalyzerSubscriptionRequestStatus,
    sendGetInboxAnalyzerPlansRequestStatus,
    sendGetInboxAnalyzerSubscriptionResponse,
    selectedPlanDetails,
  });

  const subscriptionStep = () =>
    modifySubscriptionStep === ModifySubscriptionSteps.SelectPlan ? (
      <div className="d-flex justify-content-center mt-4">
        <SelectPlan
          planCycle={planCycle}
          onSelectPlan={(planCode, planType, planName, planMode, action) =>
            handelSelectPlan(planCode, planType, planName, planMode, action)
          }
          sendGetAccountSubscriptionResponse={
            sendGetInboxAnalyzerSubscriptionResponse
          }
          sendGetPlansResponse={sendGetInboxAnalyzerPlansResponse}
        />
      </div>
    ) : (
      <InboxRadarUpgradePlan
        containerProps={containerProps}
        planCycle={planCycle}
        isRedirectedFromEmailAccounts={
          location.state?.isRedirectedFromEmailAccounts
        }
      />
    );

  if (isRequestPending) {
    return <Spinner />;
  }

  if (
    isInboxRadarBasedPlanVisible(
      sendGetInboxAnalyzerSubscriptionResponse?.planCode,
      sendGetInboxAnalyzerSubscriptionResponse?.planType,
      subscriptionPlanStatus,
    )
  ) {
    return <InboxRadarPlans />;
  }

  return (
    <>
      <div className="d-flex flex-column align-items-center">
        {/* {isColdEmailPlanBannerShow(
          sendGetInboxAnalyzerSubscriptionResponse?.planCode,
        ) && <ColdEmailPlanExploreBanner />}
        <ModifySubscriptionProgressBar
          modifySubscriptionStep={modifySubscriptionStep}
          onStepChange={(step) => handleModifySubscriptionStep(step)}
          disabledStep={getDisabledTab(
            sendGetInboxAnalyzerSubscriptionResponse?.planCode,
            sendGetInboxAnalyzerSubscriptionResponse?.planType,
          )}
        /> */}
        {planCycle !== BillingOption.LIFETIME && (
          <div className="d-flex flex-column align-items-center mt-4">
            <div className="modify-subscription__highlight-container d-flex justify-content-center align-items-center position-relative font-italic">
              <span className="semibold-1 font-8 orange-txt-16">
                {t(`messages.${getHighlightMessage(planCycle)}`)}
              </span>
              <div className="modify-subscription__circle">
                <ImageIcon src={Images.HalfCircle} />
              </div>
            </div>
            <RadioButtonTransfer
              options={billingOptions}
              onChange={handelBillingOption}
              value={planCycle}
              disabledLabel={getDisabledLabel(
                sendGetInboxAnalyzerSubscriptionResponse,
              )}
            />
          </div>
        )}
      </div>

      {subscriptionStep()}
    </>
  );
};

export default ModifyInboxRadarSubscription;
